import React from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Lock, Star, Zap } from 'lucide-react'

interface LoginPromptModalProps {
  isOpen: boolean
  onClose: () => void
  remainingUsage?: number
}

const LoginPromptModal: React.FC<LoginPromptModalProps> = ({
  isOpen,
  onClose,
  remainingUsage = 0
}) => {
  const navigate = useNavigate()

  if (!isOpen) return null

  const handleLogin = () => {
    navigate('/auth?from=' + encodeURIComponent(window.location.pathname))
    onClose()
  }

  const handleSignUp = () => {
    navigate('/auth?from=' + encodeURIComponent(window.location.pathname))
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full flex items-center justify-center mb-6">
            <Lock className="h-8 w-8 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {remainingUsage > 0 ? 'Unlock Unlimited AI Access' : 'AI Usage Limit Reached'}
          </h2>

          {remainingUsage > 0 ? (
            <p className="text-gray-600 mb-6">
              You have <span className="font-semibold text-primary-600">{remainingUsage}</span> free AI calls remaining today. 
              Sign up to unlock unlimited access!
            </p>
          ) : (
            <p className="text-gray-600 mb-6">
              You've used all your free AI calls for today. Sign up to continue using our AI features!
            </p>
          )}

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-500 mr-2" />
              Premium Features
            </h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center">
                <Zap className="w-4 h-4 text-green-500 mr-2" />
                Unlimited AI analysis calls
              </li>
              <li className="flex items-center">
                <Zap className="w-4 h-4 text-green-500 mr-2" />
                Advanced decision insights
              </li>
              <li className="flex items-center">
                <Zap className="w-4 h-4 text-green-500 mr-2" />
                Decision history tracking
              </li>
              <li className="flex items-center">
                <Zap className="w-4 h-4 text-green-500 mr-2" />
                Priority support
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleSignUp}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Sign Up for Free
            </button>
            <button
              onClick={handleLogin}
              className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Already have an account? Sign In
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Free users get 3 AI calls per day. Sign up for unlimited access!
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPromptModal 